import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Modal, Tooltip, Dialog, DialogContent, DialogContentText, DialogTitle, Typography } from '@mui/material'
import Icon from '@mui/material/Icon'
import FlipCameraAndroidOutlinedIcon from '@mui/icons-material/FlipCameraAndroidOutlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import { useStyles } from '../Pathologist/inboxStyles'
import PatientCaseHistory from '../Common/PatientCaseHistoryModal'
import TransferCorrectCaseModal from '../LabManager/TransferCorrectCaseModal'
import { CompleteColumns } from './table_def'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import CircularProgress from '@mui/material/CircularProgress'
import { CaseStatus } from './../../../utils/displayLabels'
import {
  getCompleteData,
  clearPremerisApiCache,
  clearPremerisReportApiCache,
  setCaseData,
  clearSessionToken,
  setDraftEmpty,
  refreshCaseData,
  saveReportData,
  amendReportUpload,
} from '../../../actions'
import LaunchPremeris from '../../CaseDiagnosis/LaunchPremeris'
import { RootState } from '../../../reducers'
import CustomDatagridDateRangeFilter from '../Common/CustomDatagridDateRangeFilter'
import { DataGrid, getGridStringOperators, GridFilterItem, GridFilterModel, GridFilterOperator } from '@mui/x-data-grid'
import CustomDatagridDropDownFilter from '../Common/CustomDatagridDropDownFilter'
import SuccessSnackBar from '../../HOC/SuccesSnackBar'
import ErrorSnackBar from '../../HOC/ErrorSnackBar'

interface userObj {
  id?: number
  user_id?: string
  slide_status_id?: number
}

function Complete({ userId }) {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const dispatch = useDispatch()
  const completeLogisticsData = useSelector((state: RootState) => state.getCompleteData)
  const caseData = useSelector((state: RootState) => state.getCaseData)
  const [userDetails, setUserDetails] = useState<userObj>({})
  const procedureData = useSelector((state: RootState) => state.getFilterProcedureCodeData)
  const refreshData = useSelector((state: RootState) => state.refreshCase)
  const { display_labels } = useSelector((state: RootState) => state.appConfig)
  const [completeTransferModalOpen, setCompleteTransferModalOpen] = useState(false)
  const { role } = useSelector((state: RootState) => state.userLogin)
  const gridPageSize = 10
  const [pageValue, setPageValue] = useState(0)
  const [isFirstPage, setFirstPage] = useState(false)
  const [premerisOpen, setPremerisOpen] = useState(false)
  const [isReadOnlyMode, setReadOnlyMode] = useState(false)
  const [findUrl, setFindUrl] = useState('')
  const [loadingStatus, setLoadingStatus] = useState(false)
  const [filterValue, setFilterValue] = useState('')
  const [filterName, setFilterName] = useState('updated_datetime')
  const [sortOrder, setSortOrder] = useState('desc')
  const [isFilter, setFilter] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [isAmendFlow, setAmendFlow] = useState(false)
  const [isInitiateAmendOpen, setInitiateAmendOpen] = useState(false)
  const [openSuccessSnackBar, setOpenSuccessSnackBar] = useState(false)
  const [sortField, setSortField] = useState('updated_datetime')
  const [columnModel, setColumnModel] = useState('updated_datetime')
  const { re_amendReport_success, re_amendReport_error } = useSelector((state: RootState) => state.saveAmendResponse)
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
    items: [
      {
        id: 1,
        columnField: 'quantity',
        value: [5000, 15000],
        operatorValue: 'between',
      },
    ],
  })

  const handleCompleteTransferModalOpen = (event, row: any) => {
    setUserDetails(row)
    setCompleteTransferModalOpen(true)
  }

  const handleCompleteTransferModalClose = () => {
    setCompleteTransferModalOpen(false)
  }

  useEffect(() => {
    setFindUrl(window.location.pathname)
  }, [])

  const displayCompleteLogisticsData = completeLogisticsData?.data?.filter((val) => {
    val.index = val.case_number
    return val
  })
  const totalCount = completeLogisticsData.total_count

  const ProcedureCodeRenderer = {
    field: 'procedure_code',
    headerName: 'Case Type',
    sortable: true,
    width: 204,
    headerClassName: 'super-app-theme--header',
    disableClickEventBubbling: true,
    valueGetter: (params) => {
      const procedureCodeResults = procedureData.procedureCodeMap.filter((value) => {
        return value.id == params.row.procedure_code
      })
      return procedureCodeResults[0]?.display
    },
  }

  const CaseStatusRenderer = {
    field: 'workflow_stage_id',
    headerName: 'Case Status',
    sortable: true,
    width: 215,
    headerClassName: 'super-app-theme--header',
    disableClickEventBubbling: true,
    renderCell: (params) => {
      const caseStatus = CaseStatus(params.row.stage_id, role, display_labels)
      const statusArray = caseStatus.labelStr.split(', ')
      const style = { backgroundColor: caseStatus.labelColor }
      return (
        <>
          {statusArray.map((caseStatus) => (
            // eslint-disable-next-line react/jsx-key
            <>
              <Tooltip title={caseStatus} placement="top-start">
                <Stack direction="row" spacing={2}>
                  <Chip className={classes.chipSpacing} color="primary" style={style} label={caseStatus} />
                </Stack>
              </Tooltip>
            </>
          ))}
        </>
      )
    },
  }

  const CompleteIconRender = {
    field: '',
    headerName: 'Actions',
    disableColumnMenu: true,
    width: 158,
    headerClassName: 'super-app-theme--header',
    backgroundColor: 'white',
    disableClickEventBubbling: true,
    filterable: false,
    renderCell: (params) => {
      return (
        <>
          <Tooltip title="Case History" placement="top-start">
            <Icon onClick={(event) => handleOpen(event, params.row)}>info_outlined</Icon>
          </Tooltip>
          {(params.row.stage_id === 'PRIMARY_DIAGNOSIS_RESULTED_RESULT_RELEASED' ||
            params.row.stage_id === 'CORRECT_RESULTED_RESULT_RELEASED' ||
            params.row.stage_id === 'AMEND_RESULTED_RESULT_RELEASED') && (
            <>
              &nbsp;&nbsp;&nbsp;
              <Tooltip title="Correction" placement="top-start">
                <BuildOutlinedIcon onClick={(event) => handleCompleteTransferModalOpen(event, params.row)}>
                  Correction
                </BuildOutlinedIcon>
              </Tooltip>
            </>
          )}
          {(params.row.stage_id === 'PRIMARY_DIAGNOSIS_RESULTED_RESULT_RELEASED' ||
            params.row.stage_id === 'CORRECT_RESULTED_RESULT_RELEASED' ||
            params.row.stage_id === 'AMEND_RESULTED_RESULT_RELEASED') && (
            <>
              &nbsp;&nbsp;&nbsp;
              <Tooltip title="Initiate Amendment" placement="top-start">
                <FlipCameraAndroidOutlinedIcon onClick={(event) => handleInitiateAmend(event, params.row.case_id)}>
                  Initiate Amendment
                </FlipCameraAndroidOutlinedIcon>
              </Tooltip>
            </>
          )}
        </>
      )
    },
  }

  const handleOpen = (event, rowData: any) => {
    setUserDetails(rowData)
    setOpen(true)
  }

  const handleClose = () => setOpen(false)

  const handlePageChange = (page, details) => {
    dispatch(
      getCompleteData({
        id: `${userId}`,
        pageIndex: page + 1,
        pageSize: gridPageSize,
        filterField: filterName !== '' ? filterName : columnModel,
        filterValue: filterValue,
        caseView: 'completed',
        startDate: startDate,
        endDate: endDate,
        sortOrder: sortOrder,
        sortField: sortField,
      }),
    )
  }

  const handleInitiateAmend = (event, caseId: any) => {
    setInitiateAmendOpen(true)
    setAmendFlow(true)
    setLoadingStatus(true)
    ammendPremerisOpen(event, caseId)
  }

  const handleInitiateAmendClose = () => {
    setPremerisOpen(true)
    setInitiateAmendOpen(false)
  }

  const ammendPremerisOpen = async (event, caseId: any) => {
    setLoadingStatus(true)
    /* Emptying the get draft report data when different case is selected, to prevent caching */
    dispatch(setDraftEmpty({ draft: {} }))
    const filterAssignedResults = displayCompleteLogisticsData?.filter((value) => {
      return value.case_id == caseId
    })
    const patientName = `${filterAssignedResults[0].patient_last_name || ''}, ${
      filterAssignedResults[0].patient_first_name || ''
    }`
    await handleRefreshCases(filterAssignedResults[0].case_number + '000')
    dispatch(
      setCaseData({
        pathologist: { first_name: filterAssignedResults[0].first_name, last_name: filterAssignedResults[0].last_name },
        pathologistId: filterAssignedResults[0].user_id,
        caseNumber: filterAssignedResults[0].case_number,
        procedureCodeId: filterAssignedResults[0].procedure_code,
        workflowId: 'AMEND_INITIATED',
        patientName: patientName,
        caseId: caseId,
        nlOrderId: filterAssignedResults[0].nl_order_id,
        userCaseWorkflowId: filterAssignedResults[0].id,
        slideStatusId: filterAssignedResults[0].slide_status_id,
        mcFlag: filterAssignedResults[0].mc_flag,
        statFlag: filterAssignedResults[0].stat_flag,
      }),
    )
    setReadOnlyMode(true)
    setPremerisOpen(true)
    setLoadingStatus(false)
  }

  const handleRefreshCases = async (trfBarcode) => {
    await dispatch(refreshCaseData({ trfBarcode: trfBarcode }))
    await dispatch(clearPremerisApiCache())
    await dispatch(clearPremerisReportApiCache())

    setLoadingStatus(false)
  }

  const handlePremerisOpen = async (event, caseId: any, data) => {
    const filterAssignedResults = data.filter((value) => {
      return value.case_id == caseId
    })

    const patientName = `${filterAssignedResults[0].patient_last_name || ''}, ${
      filterAssignedResults[0].patient_first_name || ''
    }`
    setLoadingStatus(true)
    await handleRefreshCases(filterAssignedResults[0].case_number + '000')
    dispatch(
      setCaseData({
        pathologist: { first_name: filterAssignedResults[0].first_name, last_name: filterAssignedResults[0].last_name },
        pathologistId: filterAssignedResults[0].user_id,
        caseNumber: filterAssignedResults[0].case_number,
        procedureCodeId: filterAssignedResults[0].procedure_code,
        workflowId: filterAssignedResults[0].stage_id,
        patientName: patientName,
        caseId: caseId,
        nlOrderId: filterAssignedResults[0].nl_order_id,
        userCaseWorkflowId: filterAssignedResults[0].id,
        slideStatusId: filterAssignedResults[0].slide_status_id,
        mcFlag: filterAssignedResults[0].mc_flag,
        statFlag: filterAssignedResults[0].stat_flag,
      }),
    )
    setLoadingStatus(false)
    setPremerisOpen(true)
  }

  const handlePremerisClose = async () => {
    updateTrfStartDataTable()
    setInitiateAmendOpen(false)
    setAmendFlow(false)
    await dispatch(clearPremerisApiCache())
    await dispatch(clearPremerisReportApiCache())
    setPremerisOpen(false)
  }

  const updateTrfStartDataTable = async () => {
    dispatch(
      getCompleteData({
        id: `${userId}`,
        pageIndex: pageValue + 1,
        pageSize: gridPageSize,
        filterField: filterName !== '' ? filterName : 'updated_datetime',
        filterValue: filterValue,
        caseView: 'completed',
        startDate: startDate,
        endDate: endDate,
        sortOrder: 'desc',
        sortField: sortField,
      }),
    )
  }

  const onFilterChange = async (model: GridFilterModel) => {
    setFilterName('')
    setFilterValue('')
    setStartDate('')
    setEndDate('')
    setFilterModel(model)
    if (model.items.length && model.items[0].value) {
      await onFilterApply(model)
    } else {
      await clearFilter()
      setFilter(false)
    }
  }

  const clearFilter = async () => {
    //const apiRef = useGridApiContext();
    await dispatch(
      getCompleteData({
        id: `${userId}`,
        pageIndex: pageValue,
        pageSize: gridPageSize,
        filterField: 'updated_datetime',
        filterValue: null,
        caseView: 'completed',
        startDate: null,
        endDate: null,
        sortOrder: 'desc',
        sortField: sortField,
      }),
    )
    //apiRef.current.hideFilterPanel()
  }

  const onFilterApply = React.useCallback(async (model: GridFilterModel) => {
    if (model.items.length) {
      if (
        (model.items[0].columnField === 'patient_first_name' || model.items[0].columnField === 'case_number') &&
        model.items[0].value &&
        model.items[0].value.length
      ) {
        const val = model.items[0].value
        setFilterValue(val)
        const name = model.items[0].columnField
        setFilterName(name)
        await dispatch(
          getCompleteData({
            id: userId,
            caseView: 'completed',
            filterField: model.items[0].columnField,
            filterValue: val,
            startDate: null,
            endDate: null,
            sortOrder: 'desc',
            pageIndex: 0,
            pageSize: 10,
            sortField: sortField,
          }),
        )
        setFilter(true)
      }
    }
  }, [])

  const handleFilteredPageChange = async (page: any, details: any) => {
    setSortOrder(details[0].sort)

    await dispatch(
      getCompleteData({
        id: `${userId}`,
        caseView: 'completed',
        filterField: filterName !== '' ? filterName : details[0].field,
        filterValue: filterValue,
        startDate: startDate,
        endDate: endDate,
        sortOrder: details[0].sort ? details[0].sort : 'desc',
        pageIndex: page + 1,
        pageSize: gridPageSize,
        sortField: details[0].field,
      }),
    )
  }

  const handleFilter = useCallback(
    (obj) => {
      setFilter(true)
      setFilterName(obj.filterField)
      setFilterValue(obj.filterValue)
      setStartDate(obj.startDate)
      setEndDate(obj.endDate)
      setSortField(obj.sortField ? obj.sortField : 'updated_datetime')
    },
    [isFilter],
  )

  const quantityOnlyOperators: GridFilterOperator[] = [
    {
      label: 'Between',
      value: 'between',
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (!Array.isArray(filterItem.value) || filterItem.value.length !== 2) {
          return null
        }
        if (filterItem.value[0] == null || filterItem.value[1] == null) {
          return null
        }

        return null
      },
      InputComponent: CustomDatagridDateRangeFilter,
      InputComponentProps: { handleFilter, sortField: sortField, caseView: 'completed' },
    },
  ]

  const dropdownOnlyOperators: GridFilterOperator[] = [
    {
      label: 'List',
      value: 'list',
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (!Array.isArray(filterItem.value) || filterItem.value.length !== 2) {
          return null
        }
        if (filterItem.value[0] == null || filterItem.value[1] == null) {
          return null
        }

        return null
      },
      InputComponent: CustomDatagridDropDownFilter,
      InputComponentProps: { handleFilter, sortField: sortField, caseView: 'completed' },
    },
  ]

  const ColumnsCustomFilters = (columnsData: any) => {
    const newCompleteColumns = [...columnsData]

    if (newCompleteColumns.length > 0) {
      const completedDateIndex = newCompleteColumns.findIndex((col) => col.headerName === 'Time Of Release')
      const completedIndex = newCompleteColumns.findIndex((col) => col.headerName === 'Signature')
      const caseTypeIndex = newCompleteColumns.findIndex((col) => col.headerName === 'Case Type')
      const priorityIndex = newCompleteColumns.findIndex((col) => col.headerName === 'Priority')
      const workflowIndex = newCompleteColumns.findIndex((col) => col.headerName === 'Case Status')
      const statesIndex = newCompleteColumns.findIndex((col) => col.headerName === 'Location')
      const resultIndex = newCompleteColumns.findIndex((col) => col.headerName === 'Result')
      const patientNameIndex = newCompleteColumns.findIndex((col) => col.headerName === 'Patient Name')
      const trfIdIndex = newCompleteColumns.findIndex((col) => col.headerName === 'TRF ID')

      if (completedDateIndex !== -1) {
        newCompleteColumns[completedDateIndex] = {
          ...newCompleteColumns[completedDateIndex],
          filterOperators: quantityOnlyOperators,
        }
      }
      if (completedIndex !== -1) {
        newCompleteColumns[completedIndex] = {
          ...newCompleteColumns[completedIndex],
          filterOperators: dropdownOnlyOperators,
        }
      }
      if (caseTypeIndex !== -1) {
        newCompleteColumns[caseTypeIndex] = {
          ...newCompleteColumns[caseTypeIndex],
          filterOperators: dropdownOnlyOperators,
        }
      }
      if (priorityIndex !== -1) {
        newCompleteColumns[priorityIndex] = {
          ...newCompleteColumns[priorityIndex],
          filterOperators: dropdownOnlyOperators,
        }
      }
      if (workflowIndex !== -1) {
        newCompleteColumns[workflowIndex] = {
          ...newCompleteColumns[workflowIndex],
          filterOperators: dropdownOnlyOperators,
        }
      }
      if (statesIndex !== -1) {
        newCompleteColumns[statesIndex] = {
          ...newCompleteColumns[statesIndex],
          filterOperators: dropdownOnlyOperators,
        }
      }
      if (resultIndex !== -1) {
        newCompleteColumns[resultIndex] = {
          ...newCompleteColumns[resultIndex],
          filterOperators: dropdownOnlyOperators,
        }
      }
      if (patientNameIndex !== -1) {
        newCompleteColumns[patientNameIndex] = {
          ...newCompleteColumns[patientNameIndex],
          filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains'),
        }
      }
      if (trfIdIndex !== -1) {
        newCompleteColumns[trfIdIndex] = {
          ...newCompleteColumns[trfIdIndex],
          filterOperators: getGridStringOperators().filter((operator) => operator.value === 'startsWith'),
        }
      }
    }

    return newCompleteColumns
  }

  CompleteColumns.push(ProcedureCodeRenderer)
  CompleteColumns.push(CaseStatusRenderer)
  CompleteColumns.push(CompleteIconRender)

  const handleTRFComplete = () => {
    dispatch(
      saveReportData({
        trfBarcode: null,
        cptCodes: [],
        saveDraftJSON: {},
        reason: '',
        diagnosisLP: {},
        consultLP: [],
        calloutDecision: false,
        procedureCode: null,
      }),
    )
    dispatch(clearSessionToken())
    setTimeout(updateCaseStatus, 1500)
  }

  const updateCaseStatus = async () => {
    setOpenSuccessSnackBar(true)
    dispatch(
      setCaseData({
        pathologist: caseData.pathologist,
        pathologistId: caseData.pathologistId,
        caseNumber: caseData.caseNumber,
        procedureCodeId: caseData.procedureCodeId,
        workflowId: 'AMEND_RESULTED_RESULT_RELEASED',
        patientName: caseData.patientName,
        caseId: caseData.caseId,
        nlOrderId: caseData.nlOrderId,
        userCaseWorkflowId: caseData.userCaseWorkflowId,
        slideStatusId: caseData.slideStatusId,
        mcFlag: caseData.mcFlag,
        statFlag: caseData.statFlag,
      }),
    )
    dispatch(
      amendReportUpload({
        pathologistId: '',
        caseNumber: '',
        caseId: '',
        nlOrderId: '',
        userCaseWorkflowId: '',
        slideStatusId: '',
        mcFlag: '',
        statFlag: '',
        userId: '',
      }),
    )
    dispatch(
      getCompleteData({
        id: `${userId}`,
        pageIndex: pageValue,
        pageSize: gridPageSize,
        filterField: filterName !== '' ? filterName : 'updated_datetime',
        filterValue: null,
        caseView: 'completed',
        startDate: null,
        endDate: null,
        sortOrder: 'desc',
        sortField: sortField,
      }),
    )
    setTimeout(() => setOpenSuccessSnackBar(false), 2000)
  }

  return (
    <div className={classes.container}>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box>
          <PatientCaseHistory userDetails={userDetails} handleClose={handleClose} />
        </Box>
      </Modal>
      {openSuccessSnackBar && re_amendReport_success && <SuccessSnackBar message="Successfully updated case status" />}
      {openSuccessSnackBar && re_amendReport_error && <ErrorSnackBar message="Failed to update case status" />}
      <Dialog
        open={isInitiateAmendOpen}
        onClose={handleInitiateAmendClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        classes={{ paper: classes.discardDialogHeight }}
      >
        <DialogTitle className={classes.dialogTitle}>Initiating Amendment</DialogTitle>
        <DialogContent className={classes.discardDialogContent}>
          <DialogContentText id="alert-dialog-description">
            {loadingStatus && (
              <div style={{ display: 'flex' }}>
                <div style={{ marginTop: '10px' }}>
                  <CircularProgress style={{ color: '#0A688D', marginRight: '10px', height: '20px', width: '20px' }} />
                </div>
                <div>Updating the case demographic information. This will take a few moments.</div>
              </div>
            )}
          </DialogContentText>
        </DialogContent>
      </Dialog>
      <div className={classes.mt}>
        <Box
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#0d688d73',
              fontWeight: '600',
            },
            '& .MuiDataGrid-columnHeader--moving': {
              backgroundColor: 'transparent !important',
            },
            '& .MuiDataGrid-filterForm': { p: 2, justifyContent: 'flex-start' },
          }}
        >
          {displayCompleteLogisticsData && (
            <DataGrid
              disableColumnSelector
              autoHeight
              pagination
              pageSize={10}
              paginationMode="server"
              rowCount={totalCount}
              page={isFirstPage ? 0 : pageValue}
              rows={displayCompleteLogisticsData}
              columns={ColumnsCustomFilters(CompleteColumns)}
              loading={completeLogisticsData?.fetchInProgress}
              onPageChange={(page, details) => {
                setPageValue(page)
                setFirstPage(false)
                handlePageChange(page, details)
              }}
              onCellClick={(params, event) => {
                event.defaultMuiPrevented = true
                if (params.field === '') {
                  return
                } else {
                  setReadOnlyMode(true)
                  handlePremerisOpen(event, params.row.case_id, displayCompleteLogisticsData)
                }
              }}
              filterModel={filterModel}
              onFilterModelChange={(model) => onFilterChange(model)}
              componentsProps={{
                filterPanel: {
                  filterFormProps: {
                    // Customize inputs by passing props
                    columnInputProps: {
                      variant: 'outlined',
                      size: 'small',
                      sx: { mt: 'auto' },
                    },
                    operatorInputProps: {
                      variant: 'outlined',
                      size: 'small',
                      sx: { mt: 'auto' },
                    },
                    valueInputProps: {
                      required: true,
                    },
                  },
                  sx: {
                    // Customize inputs using css selectors
                    '& .MuiDataGrid-filterForm': {
                      p: 2,
                      display: 'block',
                      width: 'fit-content',
                      justifyContent: 'flex-start',
                    },
                    '& .MuiDataGrid-filterForm:nth-child(even)': {},
                    '& .MuiDataGrid-filterFormLinkOperatorInput': { mr: 2 },
                    '& .MuiDataGrid-filterFormColumnInput': { mr: 2, width: 150 },
                    '& .MuiDataGrid-filterFormOperatorInput': { mr: 2 },
                    '& .MuiDataGrid-filterFormValueInput': { width: 'fit-content' },
                  },
                },
                panel: {
                  sx: {
                    '& .MuiDataGrid-panelWrapper': { overflow: 'visible' },
                    '& .MuiDataGrid-panelContent': { overflow: 'visible', display: 'flex' },
                    '& .MuiFormControl-root': {
                      width: 'auto',
                      minWidth: '55px',
                      marginRight: '18px',
                      justifyContent: 'end',
                    },
                  },
                },
              }}
              onSortModelChange={(newSortModel) => {
                if (newSortModel.length) setSortField(newSortModel[0].field)
                setPageValue(0)
                handleFilteredPageChange(pageValue, newSortModel)
              }}
            />
          )}
        </Box>
      </div>
      <TransferCorrectCaseModal
        open={completeTransferModalOpen}
        userId={userId}
        isSearch={false}
        trfId={null}
        onClose={handleCompleteTransferModalClose}
        userDetails={userDetails}
        setFirstPage={setFirstPage}
        filterName={filterName}
        filterValue={filterValue}
        sortField={sortField}
        startDate={endDate}
        endDate={endDate}
      />
      <Modal
        open={premerisOpen}
        onClose={handlePremerisClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <LaunchPremeris
          caseView={'completed'}
          urlLink={findUrl}
          handlePremerisClose={handlePremerisClose}
          handleTRFComplete={handleTRFComplete}
          isReadOnlyMode={isReadOnlyMode}
          isAmendFlow={isAmendFlow}
        />
      </Modal>
      <Modal open={loadingStatus} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <div>
          <CircularProgress style={{ marginLeft: '45%', marginTop: '40vh', position: 'absolute', color: '#0A688D' }} />
          <Typography
            style={{
              fontWeight: 'bolder',
              color: 'rgba(255,255,255,0.8)',
              fontSize: 'larger',
              marginLeft: '35%',
              marginTop: '47vh',
              position: 'absolute',
            }}
          >
            Refreshing the case details from LabOS...
          </Typography>
        </div>
      </Modal>
    </div>
  )
}

export default Complete
